/**
 * Per-currency wallet address length rules.
 * Lengths count every character (ERC-20 / EVM addresses are 42 including "0x").
 */
const EVM_RULE = {
  min: 42,
  max: 42,
  pattern: /^0x[a-fA-F0-9]{40}$/i
}

const BTC_RULE = { min: 26, max: 62 }
const LTC_RULE = { min: 26, max: 63 }
const DOGE_RULE = { min: 26, max: 34 }
const SOL_RULE = { min: 32, max: 44 }
const TRON_RULE = { min: 34, max: 34, pattern: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ }
const XRP_RULE = { min: 25, max: 35, pattern: /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/ }
const DEFAULT_RULE = { min: 26, max: 64 }

const RULES_BY_CODE = {
  ETH: EVM_RULE,
  ETH_TEST5: EVM_RULE,
  USDT_ERC20: EVM_RULE,
  'USDT.ERC20': EVM_RULE,
  USDT: EVM_RULE,
  'BNB.BSC': EVM_RULE,
  BNB: EVM_RULE,
  'USDC.ERC20': EVM_RULE,
  USDC: EVM_RULE,
  'MATIC.POLY': EVM_RULE,
  MATIC: EVM_RULE,
  BTC: BTC_RULE,
  BTC_TEST: BTC_RULE,
  LTC: LTC_RULE,
  LTC_TEST: LTC_RULE,
  LTCT: LTC_RULE,
  DOGE: DOGE_RULE,
  SOL: SOL_RULE,
  SOL_TEST: SOL_RULE,
  'USDT.SOL': SOL_RULE,
  'USDC.TRC20': TRON_RULE,
  'USDT.TRC20': TRON_RULE,
  TRX: TRON_RULE,
  XRP: XRP_RULE
}

export const getWalletAddressRules = (currencyCode = '') => {
  const raw = String(currencyCode).trim()
  if (RULES_BY_CODE[raw]) return RULES_BY_CODE[raw]

  const code = raw.toUpperCase()
  if (RULES_BY_CODE[code]) return RULES_BY_CODE[code]
  if (code.includes('SOL')) return SOL_RULE
  if (code.includes('TRC') || code.includes('TRX')) return TRON_RULE
  if (code.includes('BTC')) return BTC_RULE
  if (code.includes('LTC')) return LTC_RULE
  if (code.includes('DOGE')) return DOGE_RULE
  if (code.includes('XRP')) return XRP_RULE
  if (/USDT|ETH|ERC|BSC|POLY|BNB|MATIC|TETHER/.test(code)) return EVM_RULE
  return DEFAULT_RULE
}

export const formatExpectedAddressLength = (rules) => {
  if (!rules) return ''
  return rules.min === rules.max ? `${rules.min}` : `${rules.min}-${rules.max}`
}

/**
 * Returns a validation error object when the address length/format is invalid.
 * @returns {null|{type: 'length'|'format', expected: string, actual: number, min: number, max: number}}
 */
export const getWalletAddressValidationError = (address, currencyCode) => {
  const trimmed = String(address || '').trim()
  const rules = getWalletAddressRules(currencyCode)
  const expected = formatExpectedAddressLength(rules)

  if (trimmed.length < rules.min || trimmed.length > rules.max) {
    return {
      type: 'length',
      expected,
      actual: trimmed.length,
      min: rules.min,
      max: rules.max
    }
  }

  if (rules.pattern && !rules.pattern.test(trimmed)) {
    return {
      type: 'format',
      expected,
      actual: trimmed.length,
      min: rules.min,
      max: rules.max
    }
  }

  return null
}
